import styles from "./index.module.css";
import "./index.css";

import React from "react";
import DialogComponent, {DIALOG_TYPE} from "Core/components/DialogComponent";
import {cloneDeep} from "lodash";
import {focusSelector} from "Core/helpers/dom";
import Button, {BUTTON_DISPLAY_TYPE, BUTTON_STYLE} from "Core/components/display/Button";
import {getStringForDisplay, isset} from "Core/helpers/data";
import Label from "Core/components/display/Label";
import {icon_font_stack_class} from "Config/app";
import {blinkBackgroundSlowAnimation} from "Core/helpers/animation";
import TextareaInput from "Core/components/input/TextareaInput";
import PropTypes from "prop-types";

/**
 * Dialog used to display an error with the full stacktrace
 */
class StacktraceDialog extends DialogComponent {
	constructor(props) {
		super(props, {
			translationPath: `StacktraceDialog`,
			dialogType: DIALOG_TYPE.MESSAGE,
		});

		this.initialState = {
			wordWrap: false,
		};
		this.state = cloneDeep(this.initialState);
	}

	componentDidMount() {
		super.componentDidMount();

		// Set focus to the copy button because it is used frequently
		focusSelector(`.${styles['wrapper']} #copy-button`);
	}

	/**
	 * Render message dialog action buttons
	 * @note Message dialog has one close style button that closes the dialog on click.
	 *
	 * @param {string|null} [closeLabel] - Label used for the close button. Default value will be loaded from translation
	 * file.
	 * @param {string|null} [closeIcon] - Optional icon used for the close button.
	 * @return {*} Action buttons JSX to use in the main render method.
	 */
	renderMessageActionButtons(closeLabel = undefined, closeIcon = undefined) {
		return this.renderActionButtons([
			{
				style: BUTTON_STYLE.ACTION,
				type: BUTTON_DISPLAY_TYPE.NONE,
				label: (isset(closeLabel) && closeLabel !== null ? closeLabel : this.t('Close', 'general')),
				icon: (closeIcon ? closeIcon : ''),
				onClick: this.close,
			}
		]);
	}

	render() {
		const {errorCode, errorMessage, errorStacktrace} = this.props;
		const {wordWrap} = this.state;

		return this.renderDialog(
			this.renderCustomIconTitle('bug'),
			(
				<div className={`report-debug-dialog-component ${styles['wrapper']}`}>
					<div className={`${styles['row']}`}>
						<Label
							content={`${this.t('errorCode')}:`}
							element="strong"
							elementProps={{className: `${styles['label']}`}}
						/>
						<Label content={getStringForDisplay(errorCode)} element="span" />
					</div>
					<div className={`${styles['row']}`}>
						<Label
							content={`${this.t('errorMessage')}:`}
							element="strong"
							elementProps={{className: `${styles['label']}`}}
						/>
						<Label content={getStringForDisplay(errorMessage)} element="span" />
					</div>
					<Label content={`${this.t('errorStacktrace')}:`} element="strong" />
					<div className={`toolbar standard ${styles['stacktraceToolbar']}`}>
						<Button
							iconProps={{
								symbolPrefix: 'icofont-',
								stackClassName: `${icon_font_stack_class} word-wrap`
							}}
							icon={['align-left', 'undo']}
							displayStyle={wordWrap ? BUTTON_STYLE.ACTION : BUTTON_STYLE.NONE}
							onClick={() => { this.setState({wordWrap: !wordWrap}).then() }}
						/>
						<Button
							id="copy-button"
							icon="copy"
							displayStyle={BUTTON_STYLE.NONE}
							onClick={() => {
								if (this.stackTraceRef) {
									this.stackTraceRef.inputRef.select();
									this.stackTraceRef.inputRef.setSelectionRange(0, 999999); // Fix for mobile devices
									if (navigator.clipboard) {
										navigator.clipboard.writeText(this.stackTraceRef.inputRef.value)
											.then(() => {
												this.stackTraceRef.inputRef.setSelectionRange(0, 0);
												blinkBackgroundSlowAnimation(this.stackTraceRef.inputRef);
											});
									} else {
										document.execCommand("copy");
										this.stackTraceRef.inputRef.setSelectionRange(0, 0);
										blinkBackgroundSlowAnimation(this.stackTraceRef.inputRef);
									}
								}
							}}
						/>
					</div>
					<TextareaInput
						value={errorStacktrace} readOnly={true}
						className={`${styles['stacktrace']} ${wordWrap ? styles['wrap'] : ''}`}
						ref={node => { this.stackTraceRef = node; }}
					/>
				</div>
			),
			this.t('Close', 'general')
		);
	}
}

/**
 * Define component's own props that can be passed to it by parent components
 */
StacktraceDialog.propTypes = {
	// Unique GUI ID of the dialog
	// @note This is automatically sent by the global Dialog component.
	dialogGUIID: PropTypes.string,
	// Dialog options
	// @note This is automatically sent by the global Dialog component.
	dialogOptions: PropTypes.object,
	// Action used to close the dialog
	// @note This is automatically sent by the global Dialog component.
	dialogCloseAction: PropTypes.func,

	// Debug data
	errorCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	errorMessage: PropTypes.string,
	errorStacktrace: PropTypes.string,

	// Events
	onClose: PropTypes.func
};

/**
 * Use this const as a 'className' option on the dialog when opening it in order to get the default style.
 * @type {string}
 */
export const stacktraceDialogClassName = 'stacktrace-dialog';
export default StacktraceDialog;